<script setup lang="ts">
import { type AnyExtension, type Editor as CoreEditor, type JSONContent } from '@tiptap/core'
import type { EditorOptions } from '@tiptap/vue-3'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { onClickOutside } from '@vueuse/core'
import { computed, onUnmounted, ref, unref, useAttrs, watch } from 'vue'
import { EDITOR_UPDATE_THROTTLE_WAIT_TIME } from '@/constants/'
import { useTiptapStore } from '@/notebook/hooks'
import { useLocale } from '@/notebook/locales'
import { type EchoEditorOnChange } from '@/notebook/type'
import { differenceBy, getCssUnitWithDefault, hasExtension, isEqual, throttle } from '@/notebook/utils/utils'
import Menubars from './Menubars.vue'
import BasicBubbleMenu from './menus/BasicBubbleMenu.vue'
import ColumnsBubbleMenu from './menus/ColumnsBubbleMenu.vue'
import ContentMenu from './menus/ContentMenu.vue'
import ImageBubbleMenu from './menus/ImageBubbleMenu.vue'
import LinkBubbleMenu from './menus/LinkBubbleMenu.vue'
import TableBubbleMenu from './menus/TableBubbleMenu.vue'
import Toolbar from './Toolbar.vue'

type KeyDownHandler = NonNullable<EditorOptions['editorProps']['handleKeyDown']>
type UpdateHandler = NonNullable<EditorOptions['onUpdate']>
type EditorProps = {
  /**
   * input value
   */
  modelValue?: string | object
  /**
   * Editor output type
   *
   * @default 'html'
   */
  output?: 'html' | 'json' | 'text'
  /**
   * dark mode
   *
   * @default false
   */

  dark?: boolean
  /**
   * Readonly
   *
   * @default false
   */
  disabled?: boolean
  /**
   * Hide Editor Toolbar
   *
   * @default false
   */
  hideToolbar?: boolean
  /**
   * Hide Editor Menubar
   *
   * @default false
   */
  hideMenubar?: boolean
  /**
   * Hide Editor Bubble Menu
   *
   * @default false
   */
  hideBubble?: boolean
  /**
   * Hide Table functionality
   *
   * @default false
   */
  hideBubbleMenu?: boolean
  /**
   * Hide Table functionality
   *
   * @default false
   */
  isEditing?: boolean
  /**
   * Show Footer Toolbar
   *
   * @default false
   */
  showFooterToolbar?: boolean
  /**
   * Hide Table functionality
   *
   * @default false
   */
  hideTable?: boolean
  /**
   * Hide Content Menu
   *
   * @default false
   */
  hideContentMenu?: boolean
  /**
   * Remove tiptap default wrapper when editor is empty eg. <p></p>
   *
   * @default false
   */
  removeDefaultWrapper?: boolean
  /**
   * Editor content maximum width
   */
  maxWidth?: string | number
  /**
   * Editor content minimum height
   */
  minHeight?: string | number
  /**
   * Editor content maximum height
   */
  maxHeight?: string | number
  /**
   * Tiptap extensions
   */
  extensions?: AnyExtension[]
  /**
   * Editor container class
   */
  editorClass?: string | string[] | Record<string, any>
  /**
   * Editor content class
   */
  contentClass?: string | string[] | Record<string, any>
}

type EditorEmits = {
  (event: 'enter'): void
  (event: 'change', value: EchoEditorOnChange): void
  (event: 'update:modelValue', value: EditorProps['modelValue']): void
  (event: 'blur'): void
}

const props = withDefaults(defineProps<EditorProps>(), {
  modelValue: '',
  output: 'html',
  dark: undefined,
  disabled: false,
  hideToolbar: false,
  hideMenubar: false,
  hideBubble: false,
  hideBubbleMenu: false,
  hideTable: false,
  hideContentMenu: false,
  removeDefaultWrapper: false,
  maxWidth: undefined,
  minHeight: undefined,
  maxHeight: undefined,
  extensions: () => [],
  editorClass: undefined,
  contentClass: undefined,
})

const emit = defineEmits<EditorEmits>()

const attrs = useAttrs()
const { state, isFullscreen } = useTiptapStore()
const isHovering = ref(false)
const { t } = useLocale()
const isDark = ref(false) // Force light theme only
const contentRef = ref<HTMLElement | null>(null)
const isEditorFocused = ref(false)

const sortExtensions = computed<AnyExtension[]>(() =>
  [...state.extensions, ...differenceBy(props.extensions, state.extensions, 'name')].map((k, i) => k.configure({ sort: i })),
)

const editor = new Editor({
  content: props.modelValue,
  editorProps: {
    handleKeyDown: throttle<KeyDownHandler>((_, event) => {
      if (event.key === 'Enter' && attrs.enter && !event.shiftKey) {
        emit('enter')

        return true
      }

      return false
    }, EDITOR_UPDATE_THROTTLE_WAIT_TIME),
  },
  onBlur: () => {
    emit('blur')

    return false
  },
  onUpdate: throttle<UpdateHandler>(({ editor }) => {
    const output = getOutput(editor, props.output)

    emit('update:modelValue', output)
    emit('change', { editor, output })
  }, EDITOR_UPDATE_THROTTLE_WAIT_TIME),
  extensions: unref(sortExtensions),
  editable: !props.disabled,
})

watch(
  () => props.dark,
  (val) => {
    if (val !== undefined) {
      isDark.value = val
    }
  },
)

const contentDynamicStyles = computed(() => ({
  ...(unref(isFullscreen)
    ? { height: '100%', overflowY: 'auto' as const }
    : {
        minHeight: getCssUnitWithDefault(props.minHeight),
        maxHeight: getCssUnitWithDefault(props.maxHeight),
        overflowY: 'auto' as const,
        scrollBehavior: 'smooth',
        scrollbarWidth: 'thin',
      }),
  maxWidth: getCssUnitWithDefault(props.maxWidth),
  width: props.maxWidth ? '100%' : undefined,
  margin: props.maxWidth ? '8px auto' : undefined,
}))

function getOutput(editor: CoreEditor, output: EditorProps['output']): string | JSONContent {
  if (props.removeDefaultWrapper) {
    if (output === 'html') return editor.isEmpty ? '' : editor.getHTML()
    if (output === 'json') return editor.isEmpty ? {} : editor.getJSON()
    if (output === 'text') return editor.isEmpty ? '' : editor.getText()

    return ''
  }
  switch (output) {
    case 'html':
      return editor.getHTML()
    case 'json':
      return editor.getJSON()
    case 'text':
      return editor.getText()
    default:
      return ''
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (!editor || isEqual(getOutput(editor, props.output), val)) return
    const { from, to } = editor.state.selection

    editor.commands.setContent(val, false)
    editor.commands.setTextSelection({ from, to })
  },
  { deep: true },
)

const isInsideColorPicker = (element: HTMLElement | null): boolean => {
  if (!element) return false

  // Check for color picker and tooltip elements
  const isPickerOrTooltip =
    // Color picker checks
    element.id === 'color-picker' ||
    element.id === 'color-picker-button' ||
    element.closest('#color-picker') ||
    element.closest('.popover-content') ||
    element.closest('.color-picker') ||
    // Radix UI components
    element.closest('[data-state="open"]') ||
    element.closest('[id^="radix-"]') ||
    element.closest('[data-radix-popper-content-wrapper]') ||
    element.tagName === 'svg' ||
    element.tagName === 'path'

  return !!isPickerOrTooltip
}

watch(
  () => props.disabled,
  (val) => editor?.setEditable(!val),
)

onClickOutside(contentRef, (event) => {
  if (!isInsideColorPicker(event.target as HTMLElement)) {
    isEditorFocused.value = false
  }
})

const handleEditorClick = () => {
  isEditorFocused.value = true
}

onUnmounted(() => editor?.destroy())

defineExpose({ editor })
</script>

<template>
  <div
    v-if="editor"
    class="echo-editor bg-muted shadow outline outline-1 relative"
    :class="[
      editorClass,
      {
        'outline-primary': editor.isFocused,
        'outline-border': !editor.isFocused,
        '!rounded-[0.5rem]': !disabled,
        'comment-editor': isEditing,
      },
    ]"
    @click="handleEditorClick"
  >
    <!--    <Preview :editor="editor" />-->
    <!--    <FindAndReplace :container-ref="contentRef" :editor="editor" />-->
    <div
      class="relative flex flex-col overflow-hidden"
      :class="{
        '!fixed bg-background inset-0 z-[10]  w-full h-full m-0 rounded-[0.5rem]': isFullscreen,
      }"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
    >
      <Menubars v-if="!hideMenubar" :editor="editor" :disabled="disabled" />
      <Toolbar
        v-if="!hideToolbar && isEditorFocused"
        :editor="editor"
        :disabled="disabled"
        class="border-b py-1 px-1 overflow-hidden"
      />
      <div class="overflow-hidden relative flex-1">
        <EditorContent
          ref="contentRef"
          :editor="editor"
          :class="contentClass"
          :style="contentDynamicStyles"
          :spellcheck="state.spellCheck"
        />
        <Toolbar
          v-if="showFooterToolbar && !disabled"
          :editor="editor"
          :disabled="disabled"
          class="bg-background-light py-1 px-1 mx-2 mb-2 rounded-[6px] overflow-hidden"
        >
          <template #after>
            <slot name="footer-toolbar" :editor="editor" />
          </template>
        </Toolbar>
        <template v-if="!hideBubble && !disabled && editor.isEditable">
          <ContentMenu v-if="!hideContentMenu" :editor="editor" class="hidden sm:block" />
          <LinkBubbleMenu :editor="editor" />
          <ColumnsBubbleMenu :editor="editor" />
          <TableBubbleMenu v-if="!hideTable" :editor="editor" />
          <!-- <AIMenu :editor="editor" /> -->
          <ImageBubbleMenu :editor="editor" />
          <BasicBubbleMenu v-if="!hideBubbleMenu" :editor="editor" />
        </template>
      </div>
      <div v-if="hasExtension(editor, 'characterCount') && !disabled" class="flex justify-between border-t p-3 items-center">
        <div class="flex flex-col">
          <div class="flex justify-end gap-3 text-sm">
            <span>
              {{ editor.storage.characterCount.characters() }}
              {{ t('editor.characters') }}
            </span>
          </div>
        </div>
        <slot name="footer" :editor="editor" />
      </div>
    </div>
  </div>
</template>
<style scoped>
.comment-editor :deep(.ProseMirror) {
  min-height: 50px !important; /* Override the default min-height */
}
</style>
<style scoped lang="postcss">
.echo-editor :deep(input) {
  @apply !bg-input !text-foreground !text-center;
}
</style>
